import { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface StepPreviewIframeProps {
  routePattern: string;
  targetId: string;
  // P23a: when an author chose the "CSS selector" tab in the step
  // dialog, this carries the raw selector. The iframe forwards it
  // to TourPreviewHighlighter via postMessage; the highlighter
  // resolves it with `document.querySelector` instead of looking
  // up a `data-tour` attribute.
  targetSelector?: string;
}

const LOAD_TIMEOUT_MS = 5000;

function buildIframeUrl(
  routePattern: string,
  targetId: string,
  targetSelector: string,
): string | null {
  const trimmed = routePattern.trim();
  if (!trimmed.startsWith("/")) return null;
  // Patterns with params/wildcards aren't navigable as-is — preview
  // only works against concrete paths. Authors testing a parametric
  // tour can use the tour-dialog's "test path" helper instead.
  if (trimmed.includes(":") || trimmed.includes("*")) return null;
  const params = new URLSearchParams({
    previewFor: "tour-step",
    ...(targetId ? { targetId } : {}),
    ...(targetSelector ? { selector: targetSelector } : {}),
  });
  return `${trimmed}?${params.toString()}`;
}

export function StepPreviewIframe({
  routePattern,
  targetId,
  targetSelector = "",
}: StepPreviewIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "failed">(
    "loading",
  );

  const trimmedSelector = targetSelector.trim();
  const trimmedTargetId = targetId.trim();

  const iframeUrl = useMemo(
    () => buildIframeUrl(routePattern, trimmedTargetId, trimmedSelector),
    [routePattern, trimmedTargetId, trimmedSelector],
  );

  useEffect(() => {
    if (!iframeUrl) return;
    setLoadState("loading");
    const timer = window.setTimeout(() => {
      setLoadState((s) => (s === "loading" ? "failed" : s));
    }, LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [iframeUrl, reloadKey]);

  // Push the new target into the already-loaded iframe via
  // postMessage so we don't full-reload on every chip click or
  // selector keystroke. Sends EITHER targetId OR selector — the
  // highlighter treats them as XOR.
  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    if (!iframeUrl) return;
    if (loadState !== "ready") return;
    iframeRef.current.contentWindow.postMessage(
      {
        type: "tour-preview:set-target",
        targetId: trimmedSelector ? null : trimmedTargetId || null,
        selector: trimmedSelector || null,
      },
      window.location.origin,
    );
  }, [trimmedTargetId, trimmedSelector, iframeUrl, loadState]);

  function handleReload() {
    setReloadKey((k) => k + 1);
  }

  if (!iframeUrl) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full p-6 text-center text-sm text-muted-foreground"
        data-testid="text-preview-route-unsupported"
      >
        <AlertTriangle className="w-5 h-5 mb-2 text-amber-500" />
        <p className="font-medium">Preview not available</p>
        <p className="text-xs mt-1">
          The tour route <code className="font-mono">{routePattern}</code>{" "}
          contains a parameter or wildcard. Pick a concrete route on the tour
          to enable preview.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="panel-step-preview">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b bg-muted/30">
        <div className="flex items-center gap-1 min-w-0 text-xs text-muted-foreground">
          <span className="font-mono truncate" data-testid="text-preview-url">{iframeUrl}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={handleReload}
            aria-label="Reload preview"
            data-testid="button-preview-reload"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => window.open(iframeUrl, "_blank", "noopener")}
            aria-label="Open preview in new tab"
            data-testid="button-preview-open-new-tab"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <div className="relative flex-1 bg-background min-h-0">
        <iframe
          key={reloadKey}
          ref={iframeRef}
          src={iframeUrl}
          title="Tour step preview"
          className="w-full h-full border-0"
          onLoad={() => setLoadState("ready")}
          data-testid="iframe-step-preview"
        />
        {loadState === "loading" && (
          <div
            className="absolute inset-0 flex flex-col gap-3 p-4 bg-background"
            data-testid="status-preview-loading"
          >
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {loadState === "failed" && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-background"
            data-testid="status-preview-failed"
          >
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <div>
              <p className="text-sm font-medium">Preview didn't load</p>
              <p className="text-xs text-muted-foreground mt-1">
                The page took longer than {LOAD_TIMEOUT_MS / 1000}s to respond.
                This usually clears with a retry.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={handleReload}
              data-testid="button-preview-retry"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Retry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
